<template>
  <ScaleLoader v-if="loading" />
  <div v-if="displayChart && !loading">
    <!-- Current header has 215px -->
    <LineChart
      :height="windowHeight - 215"
      chart-id="chart"
      v-bind="lineChartProps"
    />
    <button
      class="btn btn-custom"
      @click="resetZoom('chart')"
    >
      <i class="bi bi-arrow-clockwise" />
    </button>
    <button
      class="btn btn-custom"
      @click="exportToImg('image/png', 'chart', imei)"
    >
      <i class="bi bi-filetype-png" />
    </button>
    <button
      class="btn btn-custom"
      @click="exportToImg('image/jpeg', 'chart', imei)"
    >
      <i class="bi bi-filetype-jpg" />
    </button>
    <button
      class="btn btn-custom"
      @click="exportCsv()"
    >
      <i class="bi bi-filetype-csv" />
    </button>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, inject, PropType, ref} from 'vue'
import { getSpeedReport } from '@/crud/getters'
import { LineChart, useLineChart } from 'vue-chart-3'
import { Chart, ChartData, ChartOptions, registerables } from 'chart.js'
import 'chartjs-adapter-moment'
import zoomPlugin from 'chartjs-plugin-zoom'
import { useI18n } from 'vue-i18n'
import { useWindowSize } from 'vue-window-size'
import { exportToImg, resetZoom } from '@/utils/chart'
import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'
import { generateCsv, getHumanDate, getHumanDateTime,getDateTimeForChartJs } from '@/utils/tools'

Chart.register(...registerables)
Chart.register(zoomPlugin)
Chart.register({
  id: 'customBackground',
  beforeDraw: (c) => {
    const ctx = c.ctx
    ctx.fillStyle = '#f5f8fa'
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height)
  }
})

export default defineComponent({
  name: 'Speed',
  components: {
    LineChart,
    ScaleLoader
  },
  props: {
    imei: BigInt as unknown as PropType<bigint>,
    dateFrom: String,
    dateTo: String,
    vehicle: Object,
    reportType: String
  },
  data() {
    return {
      returnedData: null
    };
  },
  async setup(props) {
    const i18n = useI18n()
    const { height } = useWindowSize()
    const swal: any | undefined = inject('$swal')
    let displayChart = false
    let loading = true
    const returnedData = ref(null);

    /**
     * Getter
     */
    if (props.imei && props.dateFrom && props.dateTo) {
      const returned = await getSpeedReport((props.imei as bigint), props.dateFrom, props.dateTo)
          .then((res) => {
            loading = false
            returnedData.value = res.data;
            return res.data
          })
          .catch(() => {
            swal.fire({
              icon: 'warning',
              title: 'Oops...',
              text: i18n.t('components.error')
            })
            loading = false
          })

      /**
       * Chart.js
       */
      const dataValues: Array<any> = []
      if (returned) {
        returned.forEach((v) => {
          dataValues.push({ x: getDateTimeForChartJs(v.device_timestamp), y: v.basic_speed })
        })

        const chartData = computed<ChartData<'line'>>(() => ({
          datasets: [
            {
              data: dataValues,
              yAxisID: 'speed',
              label: i18n.t('components.reports.speed.speed'),
              borderColor: 'rgba(246, 127, 1, 0.85)',
              backgroundColor: 'rgba(246, 127, 1, 0.85)',
              borderWidth: 2,
              tension: 0
            }
          ]
        }))

        const options = computed<ChartOptions<'line'>>(() => ({
          scales: {
            x: {
              type: 'time',
              time: {
                tooltipFormat: 'DD/MM/YYYY HH:mm',
                displayFormats: {
                  'minute': 'HH:mm',
                  'hour': 'DD/MM HH:mm',
                  'day': 'DD/MM'
                }
              }
            },
            speed: {
              type: 'linear',
              position: 'left',
              suggestedMin: 0,
              suggestedMax: 100,
              title: {
                display: true,
                text: i18n.t('components.reports.speed.speed'),
                color: 'rgba(246, 127, 1, 0.85)',
                font: {
                  weight: 'bold'
                }
              },
              ticks: {
                color: 'rgba(246, 127, 1, 0.85)',
                font: {
                  weight: 'bold'
                },
                callback: function (value) {
                  return value + ' KM/H'
                }
              }
            }
          },
          plugins: {
            legend: {
              position: 'bottom'
            },
            title: {
              display: true,
              text: i18n.t('components.reports.speed.speedReport'),
              color: '#000',
              font: {
                weight: 'bold',
                size: 24
              }
            },
            zoom: {
              pan: {
                enabled: true,
                mode: 'x'
              },
              zoom: {
                wheel: {
                  enabled: true
                },
                pinch: {
                  enabled: true
                },
                mode: 'x'
              }
            }
          },
          showLine: true,
          elements: {
            point: {
              radius: 0,
              hitRadius: 5
            }
          }
        }))

        const { lineChartProps, lineChartRef } = useLineChart({
          chartData,
          options
        })

        displayChart = true
        return {
          displayChart,
          options,
          chartData,
          lineChartProps,
          lineChartRef,
          windowHeight: height,
          loading,
          returnedData,
          i18n
        }
      }
      return { displayChart, loading }
    }
  },
  methods: {
    getHumanDateTime: getHumanDateTime,
    getHumanDate: getHumanDate,
    exportCsv() {
      if (this.returnedData) {
        const filename = 'speed_' + this.getHumanDate(Date.now() / 1000);
        const locale = this.$i18n.locale;
        generateCsv(this.returnedData, filename, this.$t, locale);
      }
    },
    resetZoom: resetZoom,
    exportToImg: exportToImg
  }
})
</script>
<style lang="scss" scoped>
.btn-custom {
  background: #f67f01;
  padding: 7px 10px 7px 13px !important;
  color: white;
  display: inline-block;
  margin: 0 2px;

  i {
    color: white;
    font-size: 1.5rem;
  }
}
</style>